/**
 * openMap({ lat, lng, label, address }) — cross-platform "open in the
 * native maps app" helper.
 *
 * iter134p — user does NOT want turn-by-turn navigation to auto-start
 * when the rider taps "Pickup Location" / "Delivery Location". They
 * want the Google Maps ROUTE PREVIEW screen (with ETA, distance and
 * alternate routes) so they can review the trip before committing to
 * start navigation.
 *
 * Root cause: the previous Android fallback chain launched
 * ``google.navigation:q=lat,lng`` which is Google Maps' shortcut for
 * "start turn-by-turn navigation to the destination right now" —
 * skipping the preview screen entirely.
 *
 * Fix: swap the Android deep-link to Google's Universal Maps Intent
 * ``https://www.google.com/maps/dir/?api=1&destination=lat,lng`` —
 * this opens the Google Maps app (Android App Links verified for
 * google.com/maps) directly on the route-preview screen. Only when
 * the user taps "Start" does turn-by-turn begin.
 *
 * New fallback chain (Android):
 *   a. ``https://www.google.com/maps/dir/?api=1&destination=lat,lng``
 *      via AppLauncher — opens Google Maps on the route-preview
 *      screen. No chooser (Android App Links dispatches directly
 *      when Google Maps is installed & verified).
 *   b. Same URL via ``location.href`` — Capacitor's
 *      ``shouldOverrideUrlLoading`` fires Intent.ACTION_VIEW, which
 *      still resolves to the verified Google Maps handler.
 *   c. ``geo:lat,lng?q=lat,lng(label)`` — generic pin/preview (may
 *      show a chooser if Google Maps isn't installed).
 *   d. Chrome Custom Tabs on the https URL — last-resort.
 *
 * iOS keeps ``maps://`` (Apple Maps daddr), which opens the Apple
 * Maps route-preview screen and waits for the user to tap "Go".
 */

const _CAP = () => (typeof window !== "undefined" ? window.Capacitor : null);

/**
 * True only for a real coordinate value. An address saved without a map pin has ``lat: null`` —
 * and ``Number(null)`` is ``0``, so the old ``Number.isFinite(Number(lat))`` check accepted it and
 * built ``destination=null,null``. Google Maps can't parse that as coordinates, treats it as a TEXT
 * SEARCH, and routes to whatever business matches — production, Sep 2026: every coordinate-less
 * Witty order navigated to a company literally named "Null Innovation" in Hyderabad, 100 km from
 * the customer. ``null``, ``undefined`` and ``""`` (all of which Number() maps to 0 or NaN-free
 * values) must count as MISSING so navigation falls back to the address text instead.
 */
export function isRealCoord(v) {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    return Number.isFinite(Number(v));
}

async function _launchNative(url) {
    // Returns true when the plugin call reported ``completed: true``.
    try {
        const mod = await import("@capacitor/app-launcher");
        if (mod && mod.AppLauncher && typeof mod.AppLauncher.openUrl === "function") {
            const r = await mod.AppLauncher.openUrl({ url });
            return !!(r && r.completed);
        }
    } catch { /* fall through */ }
    return false;
}

async function _openInBrowser(url) {
    try {
        const mod = await import("@capacitor/browser");
        if (mod && mod.Browser && typeof mod.Browser.open === "function") {
            await mod.Browser.open({ url });
            return true;
        }
    } catch { /* fall through */ }
    return false;
}

export async function openMap({ lat, lng, label, address, kind }) {
    const cap = _CAP();
    const isNative = cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform();
    const platform = cap && typeof cap.getPlatform === "function" ? cap.getPlatform() : "web";
    const hasCoords = isRealCoord(lat) && isRealCoord(lng);

    // iter134e3 — Rider app fix. When the caller passes ``kind``
    // ("pickup" or "drop"), coordinates are REQUIRED — falling back
    // to a name-based search opens a nearby place with the same
    // name (e.g. "Ayushya Restaurant") instead of the exact
    // lat/lng saved when the order was placed. Show a specific
    // toast and abort navigation entirely.
    if (kind && !hasCoords) {
        const message = kind === "pickup"
            ? "Pickup location not provided."
            : kind === "drop"
                ? "Delivery location not provided."
                : "Location coordinates not available.";
        try {
            const { toast } = await import("sonner");
            toast.error(message, { duration: 4000 });
        } catch { /* nothing else to do */ }
        return;
    }

    // Canonical https URL — universal fallback.
    let webUrl;
    if (hasCoords) {
        webUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    } else if (label || address) {
        webUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label || address)}`;
    } else {
        webUrl = "https://www.google.com/maps";
    }

    if (!isNative) {
        try { window.open(webUrl, "_blank", "noopener,noreferrer"); }
        catch { window.location.href = webUrl; }
        return;
    }

    // -------- Native (Capacitor) --------

    if (platform === "ios") {
        // iOS — Apple Maps is single-app, no chooser dialog.
        const iosUrl = hasCoords
            ? `maps://?daddr=${lat},${lng}`
            : `maps://?q=${encodeURIComponent(label || address || "")}`;
        if (await _launchNative(iosUrl)) return;
        // AppLauncher isn't cap-synced yet — fall through to
        // location.href which iOS WKWebView delegates to Apple Maps
        // for the ``maps://`` scheme.
        try { window.location.href = iosUrl; return; } catch { /* ignore */ }
        if (await _openInBrowser(webUrl)) return;
        return;   // give up quietly — no false-error toast on iOS
    }

    // Android — route-preview strategy (iter134p).
    //
    // Google's Universal Maps Intent —
    //   https://www.google.com/maps/dir/?api=1&destination=<lat,lng>
    // opens Google Maps on the ROUTE-PREVIEW screen with ETA,
    // distance and alternate routes. Adding ``&dir_action=navigate``
    // (which we deliberately DO NOT do) would skip preview and
    // auto-start turn-by-turn — the exact behaviour the user asked
    // us to stop.
    //
    // On Android, google.com/maps is a verified App Link for Google
    // Maps (com.google.android.apps.maps), so opening this https URL
    // via ``AppLauncher.openUrl`` / Intent.ACTION_VIEW dispatches
    // directly to Google Maps in-app — no browser round-trip and no
    // chooser dialog (other map apps do not claim google.com/maps).
    const previewUrl = hasCoords
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        : (label || address)
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(label || address)}`
            : null;

    // Generic geo: URI — used only as a fallback when Google Maps
    // isn't installed or App Links aren't verified. This opens a
    // location PIN (still preview-style, never turn-by-turn) and may
    // prompt the OS chooser if multiple map apps handle geo:.
    const enc = encodeURIComponent(label || address || (hasCoords ? `${lat},${lng}` : "search"));
    const geoUrl = hasCoords
        ? `geo:${lat},${lng}?q=${lat},${lng}(${enc})`
        : `geo:0,0?q=${enc}`;

    // 1) Preview https URL via AppLauncher plugin — dispatches to
    //    the verified Google Maps App Link handler when available.
    if (previewUrl && await _launchNative(previewUrl)) return;

    // 2) Preview URL via ``location.href``. Capacitor's
    //    ``BridgeWebViewClient.shouldOverrideUrlLoading`` intercepts
    //    the navigation and fires Intent.ACTION_VIEW — again
    //    resolving to the Google Maps App Link (route-preview UI).
    if (previewUrl) {
        try {
            window.location.href = previewUrl;
            return;   // MUST return here — falling through would
                      // trigger the "no maps app" toast below.
        } catch { /* fall through */ }
    }

    // 3) geo: via AppLauncher (may show chooser).
    if (await _launchNative(geoUrl)) return;

    // 4) geo: via location.href — same intercept trick; chooser may
    //    appear here if multiple map apps exist. Acceptable last-
    //    resort — never triggers turn-by-turn on its own.
    try {
        window.location.href = geoUrl;
        return;
    } catch { /* fall through */ }

    // 5) https via Chrome Custom Tabs — safe outside the WebView.
    if (previewUrl && await _openInBrowser(previewUrl)) return;
    if (await _openInBrowser(webUrl)) return;

    // 6) Give up silently. We only reach here if every intent-based
    //    handoff failed AND the browser plugin wasn't loadable —
    //    typically an emulator without any map app / browser at all.
    //    Toast only if we made zero progress (very rare).
    try {
        const { toast } = await import("sonner");
        toast.error(
            "No maps app found. Install Google Maps and try again.",
            { duration: 6000 },
        );
    } catch { /* nothing else to do */ }
}
